/**
 * Pinterest Tag — self-registering.
 *
 * Subscribes to PAGE_VIEW and ECOM_LINK_CLICK, gates on consent.
 */

import { warn } from '../../util/log';
import type { PinterestPixel } from '../../core/config/types';
import { on, SdkEvent } from '../../core/events/bus';
import type { PixelRegister } from './types';

declare global {
  interface Window {
    pintrk?: ((...args: unknown[]) => void) & {
      queue?: unknown[];
      version?: string;
    };
  }
}

const SCRIPT_URL = 'https://s.pinimg.com/ct/core.js';
let scriptInjected = false;

function ensurePintrk(): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { pintrk?: typeof window.pintrk };
  if (typeof w.pintrk !== 'function') {
    const stub = function (...args: unknown[]): void {
      const fn = stub as unknown as { queue: unknown[][] };
      fn.queue.push(args);
    } as unknown as Window['pintrk'];
    (stub as unknown as { queue: unknown[][]; version: string }).queue = [];
    (stub as unknown as { version: string }).version = '3.0';
    w.pintrk = stub;
  }
  if (scriptInjected) return;
  scriptInjected = true;
  try {
    const s = document.createElement('script');
    s.async = true;
    s.src = SCRIPT_URL;
    (document.head || document.documentElement).appendChild(s);
  } catch (err) {
    warn('pinterest script inject failed', err);
  }
}

// NOTE: `pintrk('track', ...)` is global — Pinterest's tag API has no
// per-pixel scoping, so multiple Pinterest pixels on one page would multiply
// events (same platform limitation as the legacy yoto integration).
export const registerPinterest: PixelRegister<PinterestPixel> = (pixel, context) => {
  let inited = false;

  on(SdkEvent.PAGE_VIEW, () => {
    if (context.getConsent() !== 'granted') return;
    try {
      ensurePintrk();
      if (!inited) {
        inited = true;
        window.pintrk!('load', pixel.pixel_id, { em: context.getIdentity().sessionId });
        window.pintrk!('page');
      }
      window.pintrk!('track', 'pagevisit');
    } catch (err) {
      warn('pinterest pageview failed', err);
    }
  });

  on(SdkEvent.ECOM_CLICKOUT, ({ advertiserCampaignId, productId }) => {
    if (context.getConsent() !== 'granted') return;
    try {
      window.pintrk?.('track', 'lead', { lead_type: advertiserCampaignId ?? productId });
    } catch (err) {
      warn('pinterest clickout failed', err);
    }
  });

  // mapped milestones only (yoto parity)
  on(SdkEvent.SESSION_BID_CENT_INCREMENT, (cents) => {
    if (context.getConsent() !== 'granted') return;
    const label = pixel.value_pixel?.[String(cents)];
    if (!label) return;
    try {
      window.pintrk?.('track', label);
    } catch (err) {
      warn('pinterest cent event failed', err);
    }
  });
};

export function _resetPinterestForTests(): void {
  scriptInjected = false;
}
